import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../lib/language';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { motion } from 'framer-motion';
import { Loader2, Calendar, Trophy, ArrowRight, Globe, Dog } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SportSeasonsPage() {
  const { t, language, setLanguage } = useLanguage();
  const [seasons, setSeasons] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSeasons = async () => {
      try {
        const res = await axios.get(`${API}/sport/public/seasons`);
        setSeasons(res.data.seasons);
      } catch (err) {
        console.error('Failed to load seasons:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchSeasons();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-background/80 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Dog className="w-6 h-6" />
            <h1 className="text-xl font-bold">Focus Sport</h1>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setLanguage(language === 'pl' ? 'en' : 'pl')}
          >
            <Globe className="w-4 h-4 mr-1" />
            {language.toUpperCase()}
          </Button>
        </div>
      </header>

      {/* Hero */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Trophy className="w-12 h-12 mx-auto mb-4 text-primary" />
            <h2 className="text-4xl font-bold mb-3">
              {language === 'pl' ? 'Galerie Sportowe' : 'Sport Galleries'}
            </h2>
            <p className="text-lg text-muted-foreground max-w-xl mx-auto">
              {language === 'pl'
                ? 'Znajdz zdjecia swojego psa z zawodow flyball i innych sportow'
                : 'Find photos of your dog from flyball competitions and other sports'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Seasons Grid */}
      <section className="px-6 pb-16">
        <div className="max-w-6xl mx-auto">
          {seasons.length === 0 ? (
            <div className="text-center py-20">
              <Calendar className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground">
                {language === 'pl' ? 'Brak aktywnych sezonow' : 'No active seasons'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {seasons.map((season, idx) => (
                <motion.div
                  key={season.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                >
                  <Link to={`/sport/season/${season.id}`}>
                    <Card className="hover:border-primary/30 transition-all cursor-pointer group">
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <h3 className="text-xl font-semibold group-hover:text-primary transition-colors">
                              {season.name}
                            </h3>
                            <p className="text-sm text-muted-foreground mt-1">
                              {season.sport_type} {season.year}
                            </p>
                          </div>
                          <Trophy className="w-5 h-5 text-muted-foreground" />
                        </div>
                        {season.description && (
                          <p className="text-sm text-muted-foreground mb-4">{season.description}</p>
                        )}
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">
                            {season.event_count} {language === 'pl' ? 'zawodow' : 'events'}
                          </span>
                          <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
